<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-[480px]"
    @close="close"
  >
    <template #header>
      <div class="flex flex-col gap-1 text-left">
        <h2 class="text-xl font-bold text-textPrimary">Reverter aprovação</h2>
        <p v-if="row" class="text-sm text-neutral50">
          Registro <span class="font-bold">#{{ row.checkId }}</span>
          <template v-if="row.doctor.name"> · {{ row.doctor.name }}</template>
          <template v-if="row.stationDate"> · plantão de {{ row.stationDate }}</template>
        </p>
      </div>
    </template>

    <div v-if="row" class="flex flex-col gap-4 text-left">
      <div
        class="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4"
        data-testid="check-validation-reverse-alert"
      >
        <svg class="w-5 h-5 text-amber-600 shrink-0 mt-0.5" viewBox="0 0 24 24" aria-hidden="true">
          <path :d="mdiAlertCircleOutline" fill="currentColor" />
        </svg>
        <span class="text-sm text-amber-800">
          Reverter devolve o registro para
          <span class="font-bold">Em análise</span>
          e ele deixa de valer como presença até uma nova decisão.
        </span>
      </div>

      <div class="flex flex-col gap-1">
        <label
          class="text-sm font-bold text-textPrimary"
          for="check-validation-reverse-justification"
        >
          Justificativa da reversão <span class="text-negative">*</span>
        </label>
        <textarea
          id="check-validation-reverse-justification"
          v-model="justification"
          class="w-full border border-gray-300 rounded-lg p-3 text-sm text-textPrimary resize-none focus:outline-none focus:ring-2 focus:ring-primary30"
          rows="3"
          maxlength="500"
          placeholder="Descreva por que a aprovação está sendo desfeita"
          data-testid="check-validation-reverse-justification-input"
        />
        <p class="text-xs text-neutral50">
          Obrigatória, com no mínimo 20 caracteres. Fica registrada no histórico do registro.
        </p>
        <span
          class="self-end text-xs whitespace-nowrap"
          :class="isValid ? 'text-neutral50' : 'text-negative'"
          data-testid="check-validation-reverse-char-counter"
        >
          {{ trimmedLength }}/{{ MIN_JUSTIFICATION_LENGTH }}
        </span>
      </div>
    </div>

    <template #footer>
      <div class="flex gap-4 w-full">
        <div class="flex-1">
          <OutlineButton
            text="Cancelar"
            width="100%"
            height="48px"
            :disabled="saving"
            data-testid="check-validation-reverse-cancel-button"
            @click="close"
          />
        </div>
        <div class="flex-1">
          <PolarButton
            polar-variant="outline"
            text="Confirmar reversão"
            block
            height="48"
            :disabled="!canSubmit"
            data-testid="check-validation-reverse-confirm-button"
            @click="submit"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { mdiAlertCircleOutline } from '@mdi/js'

import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import PolarButton from '@/components/designSystem/Buttons/PolarButton/index.vue'

import type { CheckValidationRow } from '../types'

export interface ReverseCheckPayload {
  checkId: number
  version: number
  justification: string
}

const MIN_JUSTIFICATION_LENGTH = 20

const props = defineProps<{
  modelValue: boolean
  row: CheckValidationRow | null
  saving: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  reverse: [payload: ReverseCheckPayload]
}>()

const justification = ref('')

const trimmedLength = computed(() => justification.value.trim().length)
const isValid = computed(() => trimmedLength.value >= MIN_JUSTIFICATION_LENGTH)
const canSubmit = computed(() => isValid.value && !!props.row && !props.saving)

watch(
  () => [props.row?.checkId, props.modelValue],
  () => {
    justification.value = ''
  }
)

function submit() {
  if (!canSubmit.value || !props.row) return
  emit('reverse', {
    checkId: props.row.checkId,
    version: props.row.version,
    justification: justification.value.trim()
  })
}

function close() {
  if (props.saving) return
  emit('update:modelValue', false)
}
</script>
